import App from './App.vue'
import { createApp, defineCustomElement } from 'vue'
import router from './router'
import vuetify from './plugins/vuetify'
import VueColumnsResizableVuetify from 'vue-columns-resizable-vuetify'
import AnalyzeSearchCeVue from './components/features/Inputs/AnalyzeSearch.ce.vue'
import ArtifactListScrollerCeVue from './components/features/Inputs/ArtifactListScroller.ce.vue'
import LinkCoverageBarCeVue from './components/features/Inputs/LinkCoverageBar.ce.vue'
import { FactoryManager } from './services/FactoryManager'

import '@fontsource/montserrat/300.css'
import '@fontsource/montserrat/500.css'
import '@fontsource/roboto/100.css'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import '@fontsource/roboto/900.css'

import { useEnvStore } from '@/stores/envStore'
import AuthorizationInterceptor, {
  AUTHZ_INTERCEPTOR_KEY,
} from './components/features/Authentication/services/authorization-interceptor'
import StatusCodeInterceptor, {
  STATUS_CODE_INTERCEPTOR_KEY,
} from './components/features/Authentication/services/status-code-interceptor'
import LoadingInterceptor, {
  LOADING_INTERCEPTOR_KEY,
} from './components/features/Authentication/services/loading-interceptor'
import { ServiceKeys } from './services/symbols'
import { pinia } from './stores'
import { Config as PlatformWebComponentsConfig } from '@itemis-solutions/platform-web-components'
import { createStore } from 'vuex'
import { useAuthStore } from './stores/authStore'

const envStore = useEnvStore(pinia)
await envStore.fetchEnv()

const factoryManager = new FactoryManager(
  envStore.variables.ANALYZE_API_BACKEND_URL,
  envStore.variables.ANALYZE_PLATFORM_URL,
)
PlatformWebComponentsConfig.PLATFORM_API_URL = envStore.variables.ANALYZE_PLATFORM_URL
const projectService = factoryManager.createProjectService()
const snapshotService = factoryManager.createSnapshotService()
const artifactService = factoryManager.createArtifactService()
const globalFilterService = factoryManager.createGlobalFilterService()

// Minimal Vuex store required by @itemis-solutions/platform-web-components.
// The AvatarMenu/UserMenuCard component dispatches "signOutOidc" via Vuex.
const vuexStore = createStore({
  actions: {
    signOutOidc() {
      const authStore = useAuthStore()
      authStore.signOut()
    },
  },
})

const app = createApp(App)
app
  .use(vuexStore)
  .provide(ServiceKeys.globalFilter, globalFilterService)
  .provide(ServiceKeys.snapshot, snapshotService)
  .provide(ServiceKeys.artifact, artifactService)
  .provide(ServiceKeys.project, projectService)
  .provide(ServiceKeys.upload, factoryManager.createUploadService())
  .provide(ServiceKeys.customMetrics, factoryManager.createCustomMetricService())
  .provide(ServiceKeys.providerIcon, factoryManager.createProviderIconService())
  .provide(ServiceKeys.search, factoryManager.createSearchService())
  .provide(ServiceKeys.dashboard, factoryManager.createDashboardService())
  .provide(ServiceKeys.colorConverter, factoryManager.createColorCoverterService())
  .provide(ServiceKeys.chart, factoryManager.createChartService())
  .provide(ServiceKeys.rappidContext, factoryManager.createRappidContext())
  .provide(AUTHZ_INTERCEPTOR_KEY, new AuthorizationInterceptor())
  .provide(STATUS_CODE_INTERCEPTOR_KEY, new StatusCodeInterceptor())
  .provide(LOADING_INTERCEPTOR_KEY, new LoadingInterceptor())
  .provide(ServiceKeys.globalFiltersToolbox, factoryManager.createGlobalFilterToolboxService())
  .use(VueColumnsResizableVuetify)
  .use(pinia)
  .use(router)
  .use(vuetify)
  .mount('#app')

const routeParamValidator = factoryManager.createRouteParamValidator(
  projectService,
  snapshotService,
  artifactService,
  globalFilterService,
)

const routeStoreSync = factoryManager.createRouteStoreSync(routeParamValidator)

app.provide(ServiceKeys.routeParamValidator, routeParamValidator)
app.provide(ServiceKeys.routeStoreSync, routeStoreSync)

// Currently, Vuetify cannot be used inside components that are registered as custom elements.
// This seems to be a limitation of Vuetify: https://github.com/vuetifyjs/vuetify/issues/5054
// Therefore, this custom element component aims to somewhat replicate the styles of AnalyzeSearch for use inside
// JointJS components. Once this issue is addressed, the AnalyzeSearchCE component will not need to manually rebuild the
// search input to look similar to what Vuetify provides.
customElements.define('analyze-search-ce', defineCustomElement(AnalyzeSearchCeVue))
customElements.define('artifact-list-scroller-ce', defineCustomElement(ArtifactListScrollerCeVue))
customElements.define('link-coverage-bar-ce', defineCustomElement(LinkCoverageBarCeVue))
